import { Suspense, lazy } from "react";
import { ArrowRight, Phone, Sparkles, Star, CheckCircle, Zap, Rocket, Shield, Target } from "lucide-react";

const Hero3DScene = lazy(() => import("@/components/Hero3DScene"));

const Hero = ({ scrollToSection }) => {
  const platformLogos = [
    { name: "Myntra", color: "#EE5F73" },
    { name: "Nykaa", color: "#FC2779" },
    { name: "Ajio", color: "#1A1A1A" },
    { name: "Blinkit", color: "#F8CB46" },
    { name: "Swiggy Instamart", color: "#FF5200" },
    { name: "Tata Cliq", color: "#E31837" }
  ];

  const differentiators = [
    { icon: Rocket, title: "Fast-Track Approvals", desc: "14-day guaranteed onboarding" },
    { icon: Shield, title: "Zero Rejection Policy", desc: "98% first-time approval rate" },
    { icon: Target, title: "Dedicated Growth Manager", desc: "Personal account specialist" }
  ];

  return (
    <header className="relative min-h-[90vh] pt-24 overflow-hidden bg-white" data-testid="hero-section">
      {/* Zaroori-style soft pastel blobs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-24 left-10 w-72 h-72 bg-gradient-to-br from-red-300 to-orange-300 rounded-full blur-3xl opacity-50 animate-float hidden sm:block" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-violet-300 to-indigo-300 rounded-full blur-3xl opacity-45 animate-float-delay hidden sm:block" />
        <div className="absolute top-10 right-1/4 w-64 h-64 bg-gradient-to-br from-emerald-200 to-teal-200 rounded-full blur-3xl opacity-40 hidden md:block" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-rose-100 via-orange-50 to-violet-100 rounded-full blur-3xl opacity-60 hidden lg:block" />
      </div>
      {/* 3D Floating Products Background */}
      <Suspense fallback={null}>
        <Hero3DScene />
      </Suspense>

      {/* Content Overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-4 md:pt-10 pb-10 md:pb-16">
          <div className="max-w-4xl mx-auto text-center">

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full text-[10px] md:text-xs font-black tracking-widest shadow-lg text-slate-700" data-testid="chatgpt-badge">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                #1 ON CHATGPT
                <CheckCircle size={12} className="text-emerald-500" />
              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-[10px] md:text-xs font-black tracking-widest shadow-[0_4px_20px_rgba(220,38,38,0.3)] border border-red-500">
                <Sparkles size={12} className="animate-pulse" />
                BEST ONBOARDING AGENCY
                <Star size={10} className="fill-yellow-300 text-yellow-300" />
              </div>

              <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] md:text-xs font-black tracking-widest shadow-[0_4px_20px_rgba(37,99,235,0.3)] border border-blue-500 hover:scale-105 transition-transform">
                <Zap size={12} />
                AI AUTO LISTINGS SOFTWARE
              </a>

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] md:text-xs font-black tracking-widest shadow-lg bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-yellow-400 border border-yellow-500/40" data-testid="oms-partner-badge">
                <CheckCircle size={12} className="fill-yellow-400 text-slate-900" />
                OMS PARTNER OF MYNTRA
              </div>
            </div>

            {/* HEADLINE */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-[1.05] tracking-tight">
              <span className="block text-slate-900">India's Best</span>
              <span className="block mt-2 text-gradient-cycle">
                Onboarding Agency.
              </span>
              <span className="block mt-2 text-gradient-violet">14 Days Launch.</span>
            </h1>

            {/* SUBHEADLINE */}
            <p className="text-base md:text-xl text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              <strong className="text-black">Best brand onboarding agency in India</strong> for Fashion, Beauty & FMCG. We get your brand <span className="text-red-600 font-bold">approved and selling</span> on{" "}
              <span className="font-bold text-black">Myntra, Nykaa, Ajio, Blinkit & Swiggy Instamart</span> — with India's highest 98% first-time approval rate.
            </p>

            {/* DIFFERENTIATORS */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto">
              {differentiators.map((diff, idx) => {
                const grads = ["from-red-500 to-orange-500 glow-card-red", "from-violet-500 to-indigo-500 glow-card-violet", "from-emerald-500 to-teal-500 glow-card-emerald"];
                const iconGrad = ["from-red-600 to-orange-500", "from-violet-600 to-indigo-500", "from-emerald-600 to-teal-500"][idx % 3];
                return (
                <div key={idx} className={`group flex flex-col items-center gap-2 px-4 py-3 bg-white/90 backdrop-blur-sm rounded-2xl ${grads[idx % 3].split(" ").pop()} hover:-translate-y-1 transition-all duration-300`}>
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${iconGrad} flex items-center justify-center shadow-md`}>
                    <diff.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-black text-black">{diff.title}</span>
                  <span className="text-xs text-gray-500">{diff.desc}</span>
                </div>
              );})}
            </div>

            {/* CTA BUTTONS */}
            <div className="flex flex-col sm:flex-row items-stretch gap-4 justify-center mb-6 max-w-2xl mx-auto">
              <button
                onClick={() => scrollToSection('contact')}
                className="group relative flex-1 min-h-[76px] px-8 bg-gradient-to-r from-[#FF2A42] via-[#FF4D30] to-[#FF9900] text-white rounded-2xl font-black text-lg tracking-wider uppercase shadow-[0_8px_30px_rgba(255,42,66,0.4)] hover:shadow-[0_14px_45px_rgba(255,42,66,0.55)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
                data-testid="hero-cta-button"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                <span className="relative flex items-center justify-center gap-2 h-full">
                  LAUNCH MY BRAND
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              <a
                href="tel:+919464645491"
                className="group flex-1 min-h-[76px] flex items-center justify-center gap-3 px-8 bg-white/90 backdrop-blur rounded-2xl font-black tracking-wider border border-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.08)] hover:border-orange-300 hover:shadow-[0_14px_45px_rgba(255,153,0,0.18)] hover:-translate-y-1 transition-all duration-500"
                data-testid="hero-phone-button"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                  <Phone size={18} className="text-white" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em]">Speak to Expert</div>
                  <div className="text-xl font-black text-slate-800 leading-tight">94646 45491</div>
                </div>
              </a>
            </div>

            {/* TRUST ROW */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 mb-8">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['M','N','A','B'].map((l, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500 to-orange-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-sm">{l}</div>
                  ))}
                </div>
                <span className="font-medium text-gray-700">Trusted by 1000+ Brands</span>
              </div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                ))}
                <span className="ml-1 font-medium text-gray-700">4.9/5 Rating</span>
              </div>
            </div>

            {/* PLATFORM LOGOS */}
            <div>
              <p className="text-center text-xs text-gray-400 font-bold mb-4 uppercase tracking-[0.2em]">
                We Get You Listed On India's Top Marketplaces
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {platformLogos.map((platform, idx) => (
                  <div
                    key={idx}
                    className="px-5 py-3 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-red-300 transition-all duration-300 cursor-default"
                    data-testid={`platform-logo-${idx}`}
                  >
                    <span className="font-bold text-sm" style={{ color: platform.color }}>
                      {platform.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
